/**
 * Currency utility functions for consistent currency symbol display across components
 */

/**
 * Maps currency codes to their standard symbols
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  'USD': '$',
  'EUR': '€',
  'GBP': '£',
  'JPY': '¥',
  'CAD': 'C$',
  'AUD': 'A$',
  'BRL': 'R$',
  'INR': '₹',
};

/**
 * Gets the appropriate currency symbol for a given currency code
 * @param currencyCode - The ISO 4217 currency code (e.g., 'USD', 'EUR')
 * @returns The currency symbol or the original currency code if not found
 */
export const getCurrencySymbol = (currencyCode: string): string => {
  if (!currencyCode || currencyCode.trim() === '') {
    return '';
  }
  
  const normalizedCode = currencyCode.toUpperCase().trim();
  return CURRENCY_SYMBOLS[normalizedCode] || currencyCode;
};

/**
 * Formats a price with the appropriate currency symbol
 * @param price - The price amount (number or string)
 * @param currencyCode - The ISO 4217 currency code
 * @param locale - Optional locale for number formatting (defaults to 'en-US')
 * @returns Formatted price string with currency symbol
 */
export const formatPrice = (
  price: number | string, 
  currencyCode: string, 
  locale: string = 'en-US'
): string => {
  const symbol = getCurrencySymbol(currencyCode);
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  
  if (isNaN(numericPrice)) {
    return `${symbol}0`;
  }
  
  // For currencies that typically don't use decimal places
  const noDecimalCurrencies = ['JPY'];
  
  const shouldUseDecimals = !noDecimalCurrencies.includes(currencyCode.toUpperCase());
  
  try {
    const formattedNumber = new Intl.NumberFormat(locale, {
      minimumFractionDigits: shouldUseDecimals ? 2 : 0,
      maximumFractionDigits: shouldUseDecimals ? 2 : 0,
    }).format(numericPrice);
    
    return `${symbol}${formattedNumber}`;
  } catch (error) {
    // Fallback to simple formatting if Intl is not available
    const formattedPrice = shouldUseDecimals ? numericPrice.toFixed(2) : Math.round(numericPrice).toString();
    return `${symbol}${formattedPrice}`;
  }
};

/**
 * Checks if a currency code is valid (exists in our symbol mapping)
 * @param currencyCode - The ISO 4217 currency code to validate
 * @returns True if the currency code is recognized
 */
export const isValidCurrencyCode = (currencyCode: string): boolean => {
  if (!currencyCode) {
    return false;
  }
  
  const normalizedCode = currencyCode.toUpperCase().trim();
  return normalizedCode in CURRENCY_SYMBOLS;
};

/**
 * Gets all supported currency codes
 * @returns Array of supported currency codes
 */
export const getSupportedCurrencyCodes = (): string[] => {
  return Object.keys(CURRENCY_SYMBOLS);
}; 
